import {
  useEffect,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  VisitNoteBillDTO,
} from 'dtos';
import {
  useGetVisitNoteForPatient,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  useAppendToVisitNote,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  normalizeBillingCodes,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useMixpanelTrack,
} from 'core/hooks';

function normalizeBill(bill: VisitNoteBillDTO | undefined): VisitNoteBillDTO {
  return {
    assessmentCodes: bill?.assessmentCodes ?? [],
    serviceCodes: normalizeBillingCodes(bill?.serviceCodes ?? []),
    customServiceCodes: (bill?.customServiceCodes ?? []).map(
      ({ keyId, ...item }) => item,
    ),
  } as VisitNoteBillDTO;
}

export default function useApplyVisitNote(patientId: number) {
  const [
    targetVisitNoteId,
    setTargetVisitNoteId,
  ] = useState<number | null>(null);
  const {
    appendToTextFields,
    appendToBillFields,
    appendTextField,
    appendTextFieldWithDoubleLine,
  } = useAppendToVisitNote();

  const mixpanelTrack = useMixpanelTrack();

  const {
    data: visitNote,
    refetch,
    isRefetching,
  } = useGetVisitNoteForPatient(targetVisitNoteId, patientId);

  useEffect(() => {
    if (!isNil(targetVisitNoteId)) {
      refetch();
    }
  }, [targetVisitNoteId]);

  useEffect(() => {
    if (!isNil(visitNote) && !isRefetching) {
      appendToTextFields(visitNote);
      appendToBillFields(normalizeBill(visitNote.bill));
      setTargetVisitNoteId(null);
    }
  }, [visitNote, isRefetching]);

  useEffect(() => {
    const unsubscribe = visitNoteEventChannel.on('onApplyPastNote', (visitNoteId) => {
      mixpanelTrack('AppliedPastNote');
      setTargetVisitNoteId(visitNoteId);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubTextField = visitNoteEventChannel.on('onApplyTextField', (key, value) => {
      appendTextField(key, value);
      visitNoteEventChannel.emit('onScrollToSection', key);
      mixpanelTrack('AppliedPastNoteSection');
    });
    const unsubTextFieldWithDoubleLine = visitNoteEventChannel.on(
      'onApplyTextFieldWithDoubleLine',
      (key, value) => {
        appendTextFieldWithDoubleLine(key, value);
        visitNoteEventChannel.emit('onScrollToSection', key);
        mixpanelTrack('AppliedPastNoteSection');
      },
    );
    const unsubBillField = visitNoteEventChannel.on('onApplyBillField', (key, codes) => {
      appendToBillFields({ [key]: codes } as VisitNoteBillDTO);
      visitNoteEventChannel.emit('onScrollToSection', key);
      mixpanelTrack('AppliedPastNoteSection');
    });
    return () => {
      unsubTextField();
      unsubTextFieldWithDoubleLine();
      unsubBillField();
    };
  }, [appendTextField, appendTextFieldWithDoubleLine, appendToBillFields]);

  return { isInProgress: isRefetching || !isNil(targetVisitNoteId) };
}
